import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";

/* ------------------------------------------------------------------ */
/*  Research Note 02 visuals: sequence, accumulation, interruption.   */
/*  A pathway of experience builds up; automation intersects it;      */
/*  the continuation stays unresolved.                                */
/* ------------------------------------------------------------------ */

const PATH_NODES = [
  { x: 60, y: 270, label: "TASK", above: false },
  { x: 160, y: 256, label: "EXPOSURE", above: false },
  { x: 300, y: 228, label: "PATTERN RECOGNITION", above: true },
  { x: 415, y: 192, label: "JUDGEMENT", above: true },
  { x: 500, y: 172, label: "EXPERTISE", above: true },
];

export function PathwayHero({ className = "", mobile = false }) {
  const labelFs = mobile ? 14 : 10;
  const labelLs = mobile ? 1 : 1.5;
  return (
    <svg viewBox="0 0 640 400" className={className} role="img" aria-label="A pathway from task to expertise, intersected by automation, ending unresolved">
      {/* pathway: solid through the early stages */}
      <motion.path
        d="M60,270 C 130,264 190,254 268,240"
        fill="none" stroke="#0F0F0F" strokeWidth="1.5"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.path
        d="M268,240 C 350,224 430,200 500,172"
        fill="none" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="1.5"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
      />
      {/* unresolved continuation */}
      <motion.path
        d="M500,172 C 540,158 575,148 603,142"
        fill="none" stroke="#6B6A65" strokeWidth="1" strokeDasharray="3 5"
        initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.9, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* accumulating experience nodes */}
      {PATH_NODES.map((n, i) => (
        <motion.circle
          key={n.label}
          cx={n.x} cy={n.y} r={i === PATH_NODES.length - 1 ? 4 : 3}
          fill={i === PATH_NODES.length - 1 ? "#1E4D38" : "#0F0F0F"}
          initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 + i * 0.22 }}
        />
      ))}
      {PATH_NODES.map((n, i) => (
        <motion.text
          key={`t${n.label}`}
          x={n.label.length > 10 ? n.x - 40 : n.x - 12}
          y={n.above ? n.y - 16 : n.y + 26}
          fontSize={labelFs} letterSpacing={labelLs} fill="#6B6A65"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.55 + i * 0.22 }}
        >
          {n.label}
        </motion.text>
      ))}

      {/* automation intersects the early pathway */}
      <motion.path
        d="M560,336 C 450,302 340,270 262,246"
        fill="none" stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4"
        initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.text
        x="470" y="330" fontSize={labelFs} letterSpacing={mobile ? 1.2 : 2} fill="#1E4D38"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 2.1 }}
      >
        AUTOMATION
      </motion.text>
      <motion.circle
        cx="262" cy="246" r="4.5" fill="none" stroke="#1E4D38" strokeWidth="1.5"
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 2.2 }}
      />

      {/* unresolved endpoint */}
      <motion.text
        x="610" y="152" fontSize="28" fill="#6B6A65" fontStyle="italic" fontFamily="Cormorant Garamond, serif"
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 2.3 }}
      >
        ?
      </motion.text>
    </svg>
  );
}

/* ---------------- VISUAL 02: the hidden value of a task ---------------- */

function SplitStage({ progress, at, x, y, label, mobile, above = false, endAnchor = false }) {
  const fallback = useMotionValue(0);
  const prog = progress || fallback;
  const opacity = useTransform(prog, [at, at + 0.15], [0, 1]);
  const tx = mobile && endAnchor ? 634 : x - 6;
  const ty = mobile && above ? y - 16 : y + 24;
  return (
    <motion.g style={{ opacity }}>
      <circle cx={x} cy={y} r="3" fill="#1E4D38" />
      <text
        x={tx}
        y={ty}
        fontSize={mobile ? 13 : 10}
        letterSpacing={mobile ? 1 : 1.5}
        fill="#6B6A65"
        textAnchor={mobile && endAnchor ? "end" : "start"}
      >
        {label}
      </text>
    </motion.g>
  );
}

export function TaskSplitVisual({ className = "", mobile = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.9", "end 0.55"] });
  const invPath1 = useTransform(scrollYProgress, [0, 0.25], [0, 1]);
  const invPath2 = useTransform(scrollYProgress, [0.25, 0.7], [0, 1]);
  const invLabel = useTransform(scrollYProgress, [0.05, 0.2], [0, 1]);

  return (
    <div ref={ref} className={className}>
      <svg viewBox="0 0 640 340" className="w-full" role="img" aria-label="A task splitting into a visible output and an invisible developmental output">
        {/* origin: the task */}
        <circle cx="80" cy="170" r="4.5" fill="#0F0F0F" />
        <text x="52" y="140" fontSize={mobile ? 15 : 11} letterSpacing="2" fill="#0F0F0F">A TASK</text>

        {/* visible output: short, obvious */}
        <motion.path
          d="M80,170 C 180,152 260,112 330,92"
          fill="none" stroke="#0F0F0F" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.path
          d="M330,92 C 390,82 430,78 470,76"
          fill="none" stroke="#0F0F0F" strokeOpacity="0.5" strokeWidth="1.5"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 1.0 }}>
          <circle cx="330" cy="92" r="3.5" fill="#0F0F0F" />
          <text x={mobile ? 240 : 300} y="66" fontSize={mobile ? 14 : 10} letterSpacing={mobile ? 1.2 : 1.8} fill="#0F0F0F">VISIBLE OUTPUT</text>
          <circle cx="470" cy="76" r="2.5" fill="#0F0F0F" fillOpacity="0.5" />
          <text x={mobile ? 376 : 436} y="52" fontSize={mobile ? 13 : 9} letterSpacing="1.2" fill="#6B6A65">work completed</text>
        </motion.g>

        {/* invisible output: emerges progressively with scroll */}
        <motion.path
          d="M80,170 C 190,182 270,212 340,232"
          fill="none" stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4"
          style={{ pathLength: invPath1 }}
        />
        <motion.path
          d="M340,232 C 420,246 500,254 580,258"
          fill="none" stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4" strokeOpacity="0.7"
          style={{ pathLength: invPath2 }}
        />
        <motion.g style={{ opacity: invLabel }}>
          <circle cx="340" cy="232" r="3.5" fill="#1E4D38" />
          <text x={mobile ? 220 : 288} y="212" fontSize={mobile ? 14 : 10} letterSpacing={mobile ? 1.2 : 1.8} fill="#1E4D38">INVISIBLE OUTPUT</text>
        </motion.g>
        <SplitStage progress={scrollYProgress} at={0.3} x={410} y={241} label="exposure" mobile={mobile} above />
        <SplitStage progress={scrollYProgress} at={0.45} x={470} y={249} label="learning" mobile={mobile} />
        <SplitStage progress={scrollYProgress} at={0.6} x={528} y={254} label="judgement" mobile={mobile} above />
        <SplitStage progress={scrollYProgress} at={0.72} x={586} y={258} label="capability" mobile={mobile} endAnchor />
      </svg>
    </div>
  );
}

/* ---------------- VISUAL 03: today's path vs an automated path ---------------- */

function PathwayColumn({ title, steps, resolved, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-fog mb-8">{title}</p>
      <div className="space-y-0">
        {steps.map((s, i) => (
          <motion.div
            key={i}
            className="relative pl-8 pb-7 last:pb-0"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.15 }}
          >
            {i < steps.length - 1 && (
              <span
                className={`absolute left-[4px] top-4 bottom-0 w-px ${resolved ? "bg-ink/25" : "bg-transparent border-l border-dashed border-fog/40"}`}
                aria-hidden="true"
              />
            )}
            <span
              className={`absolute left-0 top-1.5 w-[9px] h-[9px] rounded-full ${
                s.unresolved
                  ? "bg-transparent border border-fog/50"
                  : i === steps.length - 1
                    ? "bg-ember"
                    : "bg-ink/60"
              }`}
              aria-hidden="true"
            />
            {s.unresolved ? (
              <span className="font-serif italic text-2xl text-fog">?</span>
            ) : (
              <span className={`text-sm leading-relaxed ${i === steps.length - 1 ? "text-ink font-medium" : "text-ink/75"}`}>
                {s.label}
              </span>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function BeforeAfterPathway({ className = "" }) {
  return (
    <div className={`grid grid-cols-2 gap-8 md:gap-16 ${className}`} data-testid="before-after-pathway">
      <PathwayColumn
        title="Today's path"
        testid="pathway-today"
        resolved
        steps={[
          { label: "Junior work" },
          { label: "Repeated exposure" },
          { label: "Increasing responsibility" },
          { label: "Judgement" },
          { label: "Senior expertise" },
        ]}
      />
      <PathwayColumn
        title="An automated path?"
        testid="pathway-automated"
        steps={[
          { label: "Fewer junior tasks" },
          { unresolved: true },
          { unresolved: true },
          { label: "Required senior expertise" },
        ]}
      />
    </div>
  );
}

/* ---------------- Statement visual: tasks disappear, the path remains ---------------- */

export function TaskLineVisual({ className = "" }) {
  return (
    <svg viewBox="0 0 640 90" className={className} aria-hidden="true">
      <motion.line
        x1="40" y1="45" x2="600" y2="45"
        stroke="#0F0F0F" strokeOpacity="0.3" strokeWidth="1"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      />
      {[120, 230, 340, 450].map((x, i) => (
        <motion.circle
          key={x} cx={x} cy="45" r="4" fill="#0F0F0F"
          initial={{ opacity: 0.9 }}
          whileInView={{ opacity: 0.12 }}
          viewport={{ once: true }}
          transition={{ duration: 1.0, delay: 0.8 + i * 0.35 }}
        />
      ))}
      <motion.circle
        cx="545" cy="45" r="4" fill="#1E4D38"
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 2.3 }}
      />
    </svg>
  );
}

/* ---------------- VISUAL 04: task view vs pathway view ---------------- */

export function TaskVsPathway({ className = "" }) {
  return (
    <div className={`grid md:grid-cols-2 gap-12 md:gap-16 ${className}`} data-testid="task-vs-pathway">
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-fog mb-8">Task view</p>
        <div className="space-y-5">
          {["Task A", "Task B", "Task C"].map((t, i) => (
            <motion.div
              key={t}
              className="flex items-baseline gap-4"
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
            >
              <span className="text-sm text-ink/75 w-16">{t}</span>
              <span className="text-xs text-fog tracking-wide">automated <span className="text-ember">✓</span></span>
            </motion.div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-fog mb-8">Pathway view</p>
        <motion.div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-ink/80"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
        >
          {["A", "B", "C", "judgement", "expertise"].map((s, i) => (
            <span key={s} className="flex items-center gap-3">
              <span className={i >= 3 ? "text-ink font-medium" : ""}>{s}</span>
              {i < 4 && <span className="text-fog">→</span>}
            </span>
          ))}
        </motion.div>
        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          {["A", "B", "C"].map((s, i) => (
            <motion.span
              key={s}
              className="flex items-center gap-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.6 + i * 0.3 }}
            >
              <span className="font-serif italic text-xl text-fog">?</span>
              <span className="text-fog">→</span>
            </motion.span>
          ))}
          <motion.span
            className="flex items-center gap-3 text-ink/80"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 1.6 }}
          >
            judgement <span className="text-fog">→</span> <span className="text-ink font-medium">expertise</span>
          </motion.span>
        </div>
      </div>
    </div>
  );
}
